/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonButton,
  useIonRouter,
  IonText,
  IonIcon,
} from "@ionic/react";
import "./VerifyPage.css";
import {
  startKernelLogin,
  toE164,
  authErrorMessage,
  hasKernelLoginInFlight,
} from "../../services/auth";
import useAuth from "../../hooks/useAuth";
import { Preferences } from "@capacitor/preferences";
import { chevronBack } from "ionicons/icons";
import { useLocation } from "react-router";
import logo from "../../assets/icons/arc-logo-with-text.svg";
import { takeReturnTo } from "../../routes/returnTo";

/**
 * OTP length. **8, not 6** — this is the kernel's Cognito pool, which issues 8-digit email
 * OTPs (USER_AUTH flow). blaze issued 6, and this screen was built for blaze.
 *
 * ⚠️ Getting this wrong does not look like a length bug. The field silently TRUNCATES the
 * pasted/typed code, auto-submits on the last box it has, and the server answers 401 — so
 * it presents as "the code is always wrong", with a correct code and a working endpoint.
 */
const OTP_LENGTH = 8;

const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
  );
};

const VerifyPage: React.FC = () => {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [token, setToken] = useState<string>("");
  const [source, setSource] = useState<string>("");
  const [authMethod, setAuthMethod] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [autoDetectSupported, setAutoDetectSupported] =
    useState<boolean>(false);
  const [autoDetectAttempted, setAutoDetectAttempted] =
    useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(OTP_LENGTH).fill(null));
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const router = useIonRouter();
  const { verifyCode, isDesktop } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialRegisterMode = queryParams.get("register") !== "false";
  const [registerMode, setRegisterMode] = useState(initialRegisterMode);
  useEffect(() => {
    setOtp(Array(OTP_LENGTH).fill(""));
    fetchLoginSource();
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    setAutoDetectSupported("OTPCredential" in window);
    attemptAutoDetectOTP();
    startResendTimer();
  }, []);
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);
  let country_Code: any;
  const startResendTimer = () => {
    setIsResendDisabled(true);
    setResendTimer(60);

    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setIsResendDisabled(false);
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const fetchLoginSource = async () => {
    const { value: storedMethod } = await Preferences.get({
      key: "authMethod",
    });
    country_Code = await Preferences.get({ key: "CountryCode" });
    if (storedMethod) setAuthMethod(storedMethod);
    // ⚠️ The gate is now the KERNEL session, not blaze's `token` preference. blaze's
    // login/start returned a `token` that was stashed under that key; the kernel returns a
    // `session` which startKernelLogin stores itself. Left as-is, this screen bounced
    // straight back to Login on arrival — indistinguishable from the sign-in button
    // doing nothing.
    const loginInFlight = await hasKernelLoginInFlight();
    const { value: storedPhone } = await Preferences.get({
      key: storedMethod ?? "",
    });
    if (storedPhone !== null) setSource(storedPhone);
    else setSource("");
    // if anything is not stored call back to login
    if (!storedMethod || !loginInFlight || !storedPhone) {
      history.back();
    }
  };
  const attemptAutoDetectOTP = async () => {
    if (autoDetectAttempted) return;
    setAutoDetectAttempted(true);

    if ("OTPCredential" in window) {
      try {
        const abortController = new AbortController();
        const otpCredential = (await navigator.credentials.get({
          publicKey: { transport: ["sms"] } as any,
          signal: abortController.signal,
        })) as any | null;

        if (otpCredential && otpCredential.code) {
          const code = otpCredential.code;
          const codeArray = code.split("");
          setOtp(codeArray);

          if (codeArray.length === OTP_LENGTH) {
            setTimeout(() => {
              handleVerify(codeArray.join(""));
            }, 500);
          }
        }
      } catch (error) {
        console.log("Auto OTP detection failed or was cancelled:", error);
      }
    }
  };

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) {
      if (value.length === OTP_LENGTH && new RegExp(`^[0-9]{${OTP_LENGTH}}$`).test(value)) {
        const codeArray = value.split("");
        setOtp(codeArray);
        inputRefs.current[OTP_LENGTH - 1]?.focus();
        return;
      }
      value = value.slice(0, 1);
    }

    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every((digit) => digit !== "") && newOtp.length === OTP_LENGTH) {
      setTimeout(() => {
        handleVerify(newOtp.join(""));
      }, 300);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    // Handle Enter key to submit
    if (e.key === "Enter" && otp.join("").length === OTP_LENGTH && !isLoading) {
      e.preventDefault();
      handleVerify(otp.join(""));
    }
  };

  const handleVerify = async (otpCode?: string) => {
    const code = otpCode || otp.join("");
    if (code.length !== OTP_LENGTH) {
      setErrorMessage("Please enter a complete verification code");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response: any = await verifyCode(code);
      if (response && response.loginSource) {
        setSuccessMessage("Verification successful");
        // F-137: hand back the screen they originally asked for. `takeReturnTo` spends the
        // parked deep link so the NEXT sign-in starts from /home again; with nothing
        // parked it returns /home, which is the behaviour this line always had.
        router.push(takeReturnTo());
        if (authMethod) await Preferences.remove({ key: authMethod });
        await Preferences.remove({ key: "CountryCode" });
        await Preferences.remove({ key: "authMethod" });
        await Preferences.remove({ key: "token" });
      } else {
        const errorMsg =
          response && response.message ? response.message : "Incorrect code";
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      console.error("Verification error:", error);
      // Same mapping AuthContext uses — a generic string here would clobber the specific
      // reason (rate limited, consent required) that was just determined.
      setErrorMessage(authErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Only allow digits
    if (!/^\d+$/.test(pastedData)) return;

    // Take only up to 6 digits
    const digits = pastedData.slice(0, OTP_LENGTH).split("");

    // Fill into OTP state
    const newOtp = [...otp];
    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    setOtp(newOtp);

    // Focus last filled input
    if (digits.length > 0) {
      inputRefs.current[digits.length - 1]?.focus();
    }

    // If complete, trigger verification
    if (digits.length === OTP_LENGTH) {
      setTimeout(() => {
        handleVerify(digits.join(""));
      }, 300);
    }
  };

  const handleResendCode = async () => {
    if (isResendDisabled) return;
    startResendTimer();

    setErrorMessage("");
    try {
      const { value } = await Preferences.get({ key: authMethod ?? "" });
      const country_Code: any = await Preferences.get({ key: "CountryCode" });
      if (!value) {
        setErrorMessage("No phone number found to resend code.");
        history.back();
        return;
      }
      // Resend is the SAME login flow, so it goes to the kernel. It also refreshes the
      // stored session handle, which verifyKernelLogin reads — resending via blaze would
      // leave the old kernel session in place and the new code would not match it.
      if (authMethod === "email") {
        await startKernelLogin(value as string);
      } else if (authMethod === "phone" && source.startsWith("+")) {
        await startKernelLogin(toE164(country_Code as string, value as string));
      }

      setSuccessMessage("Verification code resent!");
      setTimeout(() => setSuccessMessage(""), 3000);

      setOtp(Array(OTP_LENGTH).fill(""));
      setAutoDetectAttempted(false);
      attemptAutoDetectOTP();

      // Restart the timer after successful resend
    } catch (error) {
      console.error("Error resending code:", error);
      // ⚠️ The most likely 429 in the whole app. `login_start` allows 1 per 60s per
      // identifier -- that IS the resend cooldown -- and the button is right here. A flat
      // "Please try again" sends the user straight back into the limiter and tells them
      // nothing about the wait, which the kernel supplies in Retry-After.
      setErrorMessage(authErrorMessage(error, "Failed to resend verification code. Please try again."));
    }
  };

  // Shared OTP form content
  const renderOtpForm = () => (
    <>
      {/* Only show title on mobile since desktop has header at top */}
      {!isDesktop && (
        <h2 className="verify-title">
          Enter the verification code sent to your {authMethod}.
        </h2>
      )}

      <div
        className="otp-input-container"
        style={{
          display: "flex",
          gap: isDesktop ? "12px" : "6px",
          marginBottom: "20px",
          width: "100%",
          justifyContent: isDesktop ? "center" : undefined
        }}
      >
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el: any) => (inputRefs.current[index] = el)}
            type="number"
            inputMode="numeric"
            autoComplete={isIOS() ? "one-time-code" : "off"}
            maxLength={1}
            value={digit}
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            className={isDesktop ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" : ""}
            style={{
              flex: "1 1 0",
              minWidth: 0,
              maxWidth: "48px",
              height: "48px",
              textAlign: "center",
              fontSize: isDesktop ? "28px" : "24px",
              borderRadius: isDesktop ? "12px" : "8px",
              border: isDesktop ? "2px solid #e5e7eb" : "1px solid #dadcdf",
              outline: "none",
            }}
            pattern="\d*"
          />
        ))}
      </div>

      {errorMessage && (
        <div className={isDesktop ? "bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-4 text-center" : "otp-error-message"}>
          {!isDesktop && <span style={{ marginRight: "3px", fontSize: "8.3px" }}>&#10060;</span>}
          {errorMessage}
        </div>
      )}

      {autoDetectSupported && !autoDetectAttempted && (
        <div id="otp-helper" style={{ display: "none" }}>
          <input type="hidden" autoComplete="one-time-code" />
        </div>
      )}

      <div
        className="resend-container"
        style={{
          marginBottom: "24px",
          flexDirection: "row",
          display: "flex",
          alignItems: "center",
          justifyContent: isDesktop ? "center" : undefined,
          gap: isDesktop ? "8px" : undefined,
        }}
      >
        <p className={isDesktop ? "text-gray-500" : "footer-text"} style={{ fontWeight: 500 }}>
          Didn&apos;t receive{' '}
          {authMethod === 'phone' ? 'a text' : 'an email'}?
        </p>
        <button
          onClick={handleResendCode}
          className={isDesktop ? "font-medium" : "resend-button w-36"}
          style={{
            opacity: isResendDisabled ? 0.5 : 1,
            cursor: isResendDisabled ? "not-allowed" : "pointer",
            color: isResendDisabled ? "#999" : isDesktop ? "#01b7c4" : "#0778CF",
          }}
        >
          {isResendDisabled
            ? `Send again (${resendTimer}s)`
            : "Send again"}
        </button>
      </div>

      {successMessage && (
        <div
          className={isDesktop ? "bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-4 text-center" : "success-message"}
          style={{
            transition: "opacity 0.5s",
            opacity: successMessage ? 1 : 0,
          }}
        >
          {successMessage}
        </div>
      )}

      <div className="flex justify-center">
        <IonButton
          className={isDesktop ? "primary-button !w-full rounded-xl text-lg font-semibold" : "sign-in-btn max-w-[500px]"}
          expand="block"
          onClick={() => handleVerify()}
          disabled={isLoading || otp.join("").length !== OTP_LENGTH}
        >
          {isLoading ? "Verifying..." : "Verify"}
        </IonButton>
      </div>

      <div
        className="flex justify-center align-items-center"
        style={{
          marginTop: isDesktop ? "24px" : "40px",
          display: "flex",
        }}
      >
        <button
          onClick={() =>
            router.push(
              "/login" +
              (registerMode ? "?register=" + registerMode : "")
            )
          }
          className={isDesktop ? "flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 font-medium" : "secondary-button skip-link m-2 align-middle text-center"}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          <IonIcon icon={chevronBack} />
          <span
            style={{
              display: "inline-block",
              textAlign: "center",
              fontWeight: "bold",
            }}
          >
            Back
          </span>
        </button>
      </div>
    </>
  );

  // Desktop layout
  if (isDesktop) {
    return (
      <IonPage>
        <IonContent fullscreen className="welcome-content">
          <div
            className="min-h-screen bg-gradient-to-br from-[#f4f7fa] via-[#e9f0f5] to-[#d6e6f2]
              flex items-center justify-center p-3
              before:inset-0
              before:bg-[radial-gradient(ellipse_at_bottom_right,_rgba(1,183,196,0.1)_0%,_transparent_50%)]
              before:opacity-100
              relative overflow-hidden"
          >
            <div className="bg-white shadow-2xl rounded-3xl overflow-hidden grid grid-cols-12 w-full max-w-6xl">
              {/* Left Side - Branding with Logo */}
              <div
                className="hidden min-[901px]:flex min-[901px]:col-span-5 text-white flex-col p-8 items-center justify-center relative overflow-hidden"
                style={{
                  background: "linear-gradient(90deg, #01b7c4 0%, #00acd7 100%)",
                }}
              >
                <div className="z-10 flex flex-col items-center justify-center h-full w-full text-center">
                  {/* Logo and Name */}
                  <img src={logo} alt="Arc Connect Logo" className="w-28 h-28 mb-6 rounded-2xl shadow-md bg-white px-1" />
                  <p className="text-lg mb-8 opacity-80">
                    Verify your identity to continue
                  </p>

                  <div className="mt-4 text-sm opacity-70">
                    Your security is our priority
                  </div>
                </div>
              </div>

              {/* Right Side - Verification Form */}
              <div className="col-span-12 min-[901px]:col-span-7 p-6 min-[901px]:p-12 flex flex-col">
                {/* Header at Top */}
                <div className="mb-8">
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    Verify Your {authMethod === "email" ? "Email" : "Phone"}
                  </h1>
                  <p className="text-lg text-gray-600">
                    Enter the verification code sent to your {authMethod}
                  </p>
                </div>

                <div className="space-y-6 flex-1">
                  {renderOtpForm()}
                </div>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Mobile layout
  return (
    <IonPage>
      <IonContent fullscreen className="welcome-content">
        <div className="responsive-container">
          <div className="left-section">
            <IonText
              className="welcome-title"
              style={{
                textAlign: "left",
              }}
            >
              Verify your {authMethod}
            </IonText>
          </div>
          <div className="right-section">
            <div className="signin-card p-2">
              {renderOtpForm()}
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default VerifyPage;
